import type { Edge, Point, SimpleNode } from 'ng-diagram';
import {
  allowedChildOeTypes,
  canHoldPosition,
  canHoldStation,
} from '../../../shared';
import type {
  OeParent,
  OrgTreeNodeDto,
  OrganizationUnitType,
  PositionAssigneeDto,
} from '../../../shared';
import type { OrgChartEditRequest, OrgEditInitial } from './org-chart.types';

export const NODE_WIDTH = 240;
/** Height of an OE (orgUnit) card's header, borders included. */
export const OE_HEIGHT = 64;
/**
 * Height of a section toggle under the OE header — "n Stellen", "n Stationen" —
 * present only when the section has something to list.
 */
export const SECTION_TOGGLE_HEIGHT = 28;
/** Bottom padding of an opened section's list inside the OE card. */
export const SECTION_LIST_PADDING = 8;
/** Gap between two rows of a section (gap-1). */
export const SECTION_ROW_GAP = 4;
/** Height of a collapsed position row, its 1px borders included. */
export const POSITION_ROW_HEIGHT = 36;
/** Height of a station row, its 1px borders included. One line, never opens. */
export const STATION_ROW_HEIGHT = 36;
/** Bottom padding of a position row's opened person list. */
export const PERSON_LIST_PADDING = 4;
/** Height of a single person row inside a position row (h-8, no row gap). */
export const PERSON_ROW_HEIGHT = 32;
/** Max number of person rows shown before the list scrolls internally. */
export const MAX_VISIBLE_PERSON_ROWS = 4;

const H_GAP = 28;
/** Vertical breathing room between depth bands. */
const V_MARGIN = 70;

/** Breathing room left around the tree when centring it in the viewport. */
export const ZOOM_TO_FIT_PADDING = 100;
/** Zoom bounds — shared by the diagram config and the zoom controls. */
export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 2;
/** Scale added/removed per zoom-button press. */
export const ZOOM_STEP = 0.1;

/**
 * Every OE hanging under this one — directly, or on one of its positions.
 *
 * Both count as children: an OE attached to a position still carries this OE
 * as its parent. The root's "only one child" rule has to see both, or the one
 * on the position would not count.
 */
function childOesOf(oe: OrgTreeNodeDto): OrgTreeNodeDto[] {
  return oe.children;
}

/**
 * Whether a child OE may be created under this OE.
 *
 * Asked of the same rule the server enforces, so the menu does not offer what
 * a save would refuse. Beware: the tree may be pruned to the actor's scope, so
 * a sibling out of scope is not counted here and the server has the last word.
 */
function mayHoldChildOe(oe: OrgTreeNodeDto): boolean {
  return (
    allowedChildOeTypes({
      type: oe.type,
      hasOtherChild: childOesOf(oe).length > 0,
    }).length > 0
  );
}

export interface OrgUnitNodeData {
  kind: 'orgUnit';
  oeId: string;
  name: string;
  type: OrganizationUnitType;
  isDeletionLocked: boolean;
  /** Ancestor shown only for path context — greyed, no positions, no actions. */
  contextOnly: boolean;
  canUpdate: boolean;
  canCreateChild: boolean;
  /** Whether the placement rule leaves room for a child OE beneath this one. */
  canCreateChildOe: boolean;
  /** Whether a Stelle may sit on this unit at all — the root holds none. */
  canCreatePosition: boolean;
  /** Whether a station may sit on this unit at all — the root holds none. */
  canCreateStation: boolean;
  canDelete: boolean;
  /** The unit's Stellen, listed inside its card rather than as nodes of their own. */
  positions: NestedPositionData[];
  /** The unit's stations, listed inside its card the same way. */
  stations: NestedStationData[];
  [key: string]: unknown;
}

/** A station as one row inside the card of its OE. */
export interface NestedStationData {
  stationId: string;
  /** OE holding the station; its scope decides who may manage it. */
  oeId: string;
  name: string;
  /** Who answers for the station; empty when nobody does. */
  responsiblePersonName: string;
  /** Whether the actor may edit the station and its skill requirements. */
  canManage: boolean;
  canDelete: boolean;
}

/** A position (Stelle) as one row inside its OE card. */
export interface NestedPositionData {
  positionId: string;
  /** OE containing this position; remains the permission scope anchor. */
  parentOeId: string;
  /** Whether the position has no assignees or child OEs and may be deleted. */
  canDelete: boolean;
  name: string;
  roleCount: number;
  /** IDs of roles attached to this position. */
  roleIds: string[];
  /** Persons assigned to this position — rendered as a list under the row. */
  assignees: PositionAssigneeDto[];
  /** Whether the actor may manage this position (edit, roles, assign/move). */
  canManage: boolean;
  /**
   * Names of the OEs hanging on this position. Their edge starts at the OE
   * card, so the row has to say which of its Stellen they belong to.
   */
}

export type OrgNodeData = OrgUnitNodeData;

/** Visible person rows for a position (clamped; at least 1 so empty rows are drop targets). */
export function visiblePersonRows(assigneeCount: number): number {
  return Math.min(Math.max(assigneeCount, 1), MAX_VISIBLE_PERSON_ROWS);
}

/** Rendered height of one position row. The person list only counts when expanded. */
export function positionRowHeight(
  assigneeCount: number,
  expanded: boolean,
): number {
  const listHeight = expanded
    ? visiblePersonRows(assigneeCount) * PERSON_ROW_HEIGHT + PERSON_LIST_PADDING
    : 0;
  return POSITION_ROW_HEIGHT + listHeight;
}

/** What an OE card lists under its header, and which of those lists are open. */
export interface OeCardSections {
  positions: readonly { id: string; assignees: readonly unknown[] }[];
  positionsExpanded: boolean;
  stationCount: number;
  stationsExpanded: boolean;
}

/** One section: its toggle, and while open its rows, gaps and padding. */
function sectionHeight(
  rowHeights: readonly number[],
  expanded: boolean,
): number {
  if (rowHeights.length === 0) return 0;
  if (!expanded) return SECTION_TOGGLE_HEIGHT;
  const rows = rowHeights.reduce((sum, height) => sum + height, 0);
  const gaps = (rowHeights.length - 1) * SECTION_ROW_GAP;
  return SECTION_TOGGLE_HEIGHT + rows + gaps + SECTION_LIST_PADDING;
}

/**
 * Rendered height of an OE card: the header, then the "n Stellen" and the
 * "n Stationen" section — each only when the unit has some, each with its rows
 * only while it is open.
 */
export function oeNodeHeight(
  card: OeCardSections,
  expandedPositionIds: ReadonlySet<string>,
): number {
  const positionRows = card.positions.map((p) =>
    positionRowHeight(p.assignees.length, expandedPositionIds.has(p.id)),
  );
  const stationRows = Array.from(
    { length: card.stationCount },
    () => STATION_ROW_HEIGHT,
  );
  return (
    OE_HEIGHT +
    sectionHeight(positionRows, card.positionsExpanded) +
    sectionHeight(stationRows, card.stationsExpanded)
  );
}

interface RenderNode {
  id: string;
  type: 'orgUnit';
  data: OrgNodeData;
  children: RenderNode[];
  depth: number;
  height: number;
  x: number;
  y: number;
}

/**
 * Which parts of the chart are opened: an OE's list of Stellen, a Stelle's
 * person list, an OE's list of stations.
 */
export interface OrgChartExpansion {
  oeIds: ReadonlySet<string>;
  positionIds: ReadonlySet<string>;
  stationListOeIds: ReadonlySet<string>;
}

const NOTHING_EXPANDED: OrgChartExpansion = {
  oeIds: new Set(),
  positionIds: new Set(),
  stationListOeIds: new Set(),
};

function buildRenderTree(
  oe: OrgTreeNodeDto,
  expansion: OrgChartExpansion,
): RenderNode {
  const positions: NestedPositionData[] = oe.positions.map((p) => ({
    positionId: p.id,
    parentOeId: oe.id,
    canDelete: p.canDelete,
    name: p.name,
    roleCount: p.roleIds.length,
    roleIds: p.roleIds,
    assignees: p.assignees,
    canManage: p.canManage,
  }));
  const stations: NestedStationData[] = oe.stations.map((station) => ({
    stationId: station.id,
    oeId: oe.id,
    name: station.name,
    responsiblePersonName: station.responsiblePersonName ?? '',
    canManage: station.canManage,
    canDelete: station.canDelete,
  }));

  return {
    id: `oe:${oe.id}`,
    type: 'orgUnit',
    data: {
      kind: 'orgUnit',
      oeId: oe.id,
      name: oe.name,
      type: oe.type,
      isDeletionLocked: oe.isDeletionLocked,
      contextOnly: oe.contextOnly,
      canUpdate: oe.canUpdate,
      canCreateChild: oe.canCreateChild,
      canCreateChildOe: mayHoldChildOe(oe),
      canCreatePosition: canHoldPosition(oe.type),
      canCreateStation: canHoldStation(oe.type),
      canDelete: oe.canDelete,
      positions,
      stations,
    },
    depth: 0,
    height: oeNodeHeight(
      {
        positions: oe.positions,
        positionsExpanded: expansion.oeIds.has(oe.id),
        stationCount: stations.length,
        stationsExpanded: expansion.stationListOeIds.has(oe.id),
      },
      expansion.positionIds,
    ),
    x: 0,
    y: 0,
    // An OE hanging on one of the unit's Stellen hangs from the unit's card:
    // the Stelle is a row inside it, not a node an edge could start from.
    children: childOesOf(oe).map((child) => buildRenderTree(child, expansion)),
  };
}

/**
 * Top-down tree layout. Because position cards have variable heights, each depth
 * band is offset by the tallest node at the *previous* depth so cards never overlap
 * vertically. Leaves get sequential x slots; parents centre over their children.
 *
 * This replaced ELK's layered layout on 2026-10-28: it packed the tree tighter
 * but left a parent off-centre above its children and spread its edges along
 * the parent's bottom edge, which does not read as an org chart.
 */
function layout(root: RenderNode): void {
  // Pass 1: assign depth + collect the tallest node per depth.
  const depthMaxHeight: number[] = [];
  const assignDepth = (node: RenderNode, depth: number): void => {
    node.depth = depth;
    depthMaxHeight[depth] = Math.max(depthMaxHeight[depth] ?? 0, node.height);
    node.children.forEach((c) => assignDepth(c, depth + 1));
  };
  assignDepth(root, 0);

  // Cumulative y per depth: top of each band = sum of previous bands' heights + margins.
  const depthY: number[] = [];
  let cursorY = 0;
  for (let d = 0; d < depthMaxHeight.length; d++) {
    depthY[d] = cursorY;
    cursorY += depthMaxHeight[d] + V_MARGIN;
  }

  // Pass 2: x slots for leaves, parents centred over children.
  let cursorX = 0;
  const place = (node: RenderNode): void => {
    node.y = depthY[node.depth];
    if (node.children.length === 0) {
      node.x = cursorX;
      cursorX += NODE_WIDTH + H_GAP;
      return;
    }
    node.children.forEach(place);
    const first = node.children[0];
    const last = node.children[node.children.length - 1];
    node.x = (first.x + last.x) / 2;
  };
  place(root);
}

/**
 * The route of one edge, drawn the way an org chart is: down from the middle
 * of the parent's bottom edge to a line halfway into the gap above the
 * children, along it, and down into the middle of the child's top edge.
 *
 * Every child of a parent shares the first leg and the line, so the edges read
 * as one trunk with branches. The line sits at the same height under every
 * parent of a band, because the band below starts at one y for all of them.
 * A child straight below its parent gets a straight line.
 */
export function busRoute(
  parent: { x: number; y: number; height: number },
  child: { x: number; y: number },
): Point[] {
  const sourceX = parent.x + NODE_WIDTH / 2;
  const targetX = child.x + NODE_WIDTH / 2;
  const start = { x: sourceX, y: parent.y + parent.height };
  const end = { x: targetX, y: child.y };
  if (sourceX === targetX) return [start, end];
  const busY = child.y - V_MARGIN / 2;
  return [start, { x: sourceX, y: busY }, { x: targetX, y: busY }, end];
}

export interface DiagramData {
  nodes: SimpleNode<OrgNodeData>[];
  edges: Edge[];
}

/** Convert the org tree into ng-diagram nodes + edges with computed positions. */
export function orgTreeToDiagram(
  tree: OrgTreeNodeDto,
  expansion: OrgChartExpansion = NOTHING_EXPANDED,
): DiagramData {
  const root = buildRenderTree(tree, expansion);
  layout(root);

  const nodes: SimpleNode<OrgNodeData>[] = [];
  const edges: Edge[] = [];

  const walk = (node: RenderNode, parent: RenderNode | null): void => {
    nodes.push({
      id: node.id,
      position: { x: node.x, y: node.y },
      data: node.data,
      type: node.type,
      size: { width: NODE_WIDTH, height: node.height },
      // Heights are computed here; keep ng-diagram from re-measuring content.
      autoSize: false,
      // Context-only ancestors are inert navigation context, not editable nodes.
      draggable: !node.data.contextOnly,
    });
    if (parent) {
      edges.push({
        id: `${parent.id}->${node.id}`,
        source: parent.id,
        target: node.id,
        data: {},
        routing: 'orthogonal',
        // The points below are the route; ng-diagram only draws them.
        routingMode: 'manual',
        points: busRoute(parent, node),
      });
    }
    node.children.forEach((c) => walk(c, node));
  };

  walk(root, null);
  return { nodes, edges };
}

/** Centre point of a node given its top-left position + size. */
export function nodeCenter(node: {
  position: { x: number; y: number };
  size?: { width: number; height: number };
}): {
  x: number;
  y: number;
} {
  const w = node.size?.width ?? NODE_WIDTH;
  const h = node.size?.height ?? OE_HEIGHT;
  return { x: node.position.x + w / 2, y: node.position.y + h / 2 };
}

/**
 * What "expand all" opens: every OE that holds a Stelle, every Stelle, and
 * every OE that lists stations in its card.
 */
export function collectExpandableIds(tree: OrgTreeNodeDto | null): {
  oeIds: string[];
  positionIds: string[];
  stationListOeIds: string[];
} {
  const oeIds: string[] = [];
  const positionIds: string[] = [];
  const stationListOeIds: string[] = [];
  const walk = (oe: OrgTreeNodeDto): void => {
    if (oe.positions.length > 0) oeIds.push(oe.id);
    for (const position of oe.positions) positionIds.push(position.id);
    if (oe.stations.length > 0) stationListOeIds.push(oe.id);
    childOesOf(oe).forEach(walk);
  };
  if (tree) walk(tree);
  return { oeIds, positionIds, stationListOeIds };
}

/** Find an OE / position / station in the tree and resolve its current editable values. */
export function resolveEditInitial(
  tree: OrgTreeNodeDto | null,
  target: OrgChartEditRequest,
): OrgEditInitial | null {
  if (!tree) return null;
  const walk = (oe: OrgTreeNodeDto): OrgEditInitial | null => {
    if (target.kind === 'orgUnit' && oe.id === target.id) {
      return {
        name: oe.name,
        type: oe.type,
        legalName: oe.legalName ?? '',
        countryCode: oe.countryCode ?? '',
        timezone: oe.timezone ?? '',
        // `?? ''` not `|| ''`: a waiting period of 0 has to reach the field
        // as "0", or opening the form would show it as inherited and saving
        // would clear the decision.
        vacationWaitingPeriodMonths:
          oe.vacationWaitingPeriodMonths?.toString() ?? '',
        vacationYearType: oe.vacationYearType ?? '',
        description: oe.description ?? '',
        // An OE names nobody responsible; the field is a station's.
        responsiblePersonId: '',
        responsiblePersonName: '',
        roleIds: [],
      };
    }
    if (target.kind === 'station') {
      const station = oe.stations.find((s) => s.id === target.id);
      if (station) {
        return {
          name: station.name,
          type: 'department',
          legalName: '',
          countryCode: '',
          timezone: '',
          vacationWaitingPeriodMonths: '',
          vacationYearType: '',
          description: station.description ?? '',
          responsiblePersonId: station.responsiblePersonId ?? '',
          responsiblePersonName: station.responsiblePersonName ?? '',
          roleIds: [],
        };
      }
    }
    if (target.kind === 'position') {
      const pos = oe.positions.find((p) => p.id === target.id);
      if (pos) {
        return {
          name: pos.name,
          type: 'department',
          legalName: '',
          countryCode: '',
          timezone: '',
          vacationWaitingPeriodMonths: '',
          vacationYearType: '',
          description: pos.description ?? '',
          // A Stelle names no responsible person; the field is a station's.
          responsiblePersonId: '',
          responsiblePersonName: '',
          roleIds: pos.roleIds,
        };
      }
    }
    for (const child of oe.children) {
      const found = walk(child);
      if (found) return found;
    }
    return null;
  };
  return walk(tree);
}

/** Role ids of a position somewhere in the tree, or null when not found. */
export function findPositionRoleIds(
  tree: OrgTreeNodeDto | null,
  positionId: string,
): string[] | null {
  if (!tree) return null;
  const walk = (oe: OrgTreeNodeDto): string[] | null => {
    const pos = oe.positions.find((p) => p.id === positionId);
    if (pos) return pos.roleIds;
    for (const child of oe.children) {
      const found = walk(child);
      if (found) return found;
    }
    return null;
  };
  return walk(tree);
}

/** Active assignees of a position somewhere in the tree, or null when not found. */
export function findPositionAssignees(
  tree: OrgTreeNodeDto | null,
  positionId: string,
): PositionAssigneeDto[] | null {
  if (!tree) return null;
  const walk = (oe: OrgTreeNodeDto): PositionAssigneeDto[] | null => {
    const pos = oe.positions.find((p) => p.id === positionId);
    if (pos) return pos.assignees;
    for (const child of oe.children) {
      const found = walk(child);
      if (found) return found;
    }
    return null;
  };
  return walk(tree);
}

/**
 * Whether the actor may delete the given OE, position or station from the tree.
 * OEs use the server-computed {@link OrgTreeNodeDto.canDelete} (no children,
 * positions or stations). Positions require manage permission and no
 * assignees; stations only manage permission.
 */
export function findCanDelete(
  tree: OrgTreeNodeDto | null,
  target: OrgChartEditRequest,
): boolean | null {
  if (!tree) return null;
  const walk = (oe: OrgTreeNodeDto): boolean | null => {
    if (target.kind === 'orgUnit' && oe.id === target.id) {
      return oe.canDelete;
    }
    if (target.kind === 'position') {
      const pos = oe.positions.find((p) => p.id === target.id);
      if (pos) return pos.canDelete;
    }
    if (target.kind === 'station') {
      const station = oe.stations.find((s) => s.id === target.id);
      if (station) return station.canDelete;
    }
    for (const child of oe.children) {
      const found = walk(child);
      if (found !== null) return found;
    }
    return null;
  };
  return walk(tree);
}

/**
 * What an OE already in the tree hangs under.
 *
 * Feeds the placement rule, which needs the parent's type and whether the OE
 * is attached to a position — neither is on the node itself, both are known
 * from where the node was found.
 */
export function findOeParent(
  tree: OrgTreeNodeDto | null,
  oeId: string,
): OeParent | null {
  if (!tree) return null;
  if (tree.id === oeId) {
    return { type: null, hasOtherChild: false };
  }
  const walk = (oe: OrgTreeNodeDto): OeParent | null => {
    // "Other" than this one: a unit staying where it is must not count itself.
    const hasOtherChild = childOesOf(oe).some((c) => c.id !== oeId);
    for (const child of oe.children) {
      if (child.id === oeId) {
        return { type: oe.type, hasOtherChild };
      }
    }
    for (const child of oe.children) {
      const found = walk(child);
      if (found) return found;
    }
    return null;
  };
  return walk(tree);
}

/** What a new child created under this OE or position would hang under. */
export function findChildParent(
  tree: OrgTreeNodeDto | null,
  contextId: string,
): OeParent | null {
  if (!tree) return null;
  const walk = (oe: OrgTreeNodeDto): OeParent | null => {
    // Nothing is being placed yet, so every existing child is an "other" one.
    const hasOtherChild = childOesOf(oe).length > 0;
    if (oe.id === contextId) {
      return { type: oe.type, hasOtherChild };
    }
    for (const child of oe.children) {
      const found = walk(child);
      if (found) return found;
    }
    return null;
  };
  return walk(tree);
}
